import * as Sentry from '@sentry/react';
import type { AnyRouter } from '@tanstack/react-router';
import { env } from './env';

/**
 * Sentry init for the Vite SPA (T2.8).
 *
 * - Only initialises if `VITE_SENTRY_DSN` is set, so local dev / test runs
 *   stay quiet without env stubbing.
 * - `tanstackRouterBrowserTracingIntegration` produces transactions tagged
 *   with the matched TanStack Router pathname, so error rate / latency
 *   dashboards group cleanly per route.
 * - We expose `wrapCreateRouter` (no-op when Sentry is disabled) so `main.tsx`
 *   can wrap the router unconditionally.
 */

let initialized = false;

export function initSentry(): void {
  if (initialized) return;
  const dsn = env.sentryDsn;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: env.sentryEnvironment,
    release: env.sentryRelease ?? undefined,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
  });
  initialized = true;
}

/**
 * Wraps a TanStack Router instance so route changes generate Sentry
 * transactions. Returns the router unchanged when Sentry is not initialised.
 *
 * Sentry's first-class TanStack Router integration shipped in
 * `@sentry/react` 8+ as `tanstackRouterBrowserTracingIntegration`, but for
 * compatibility across @sentry/react versions we instead listen to the
 * router's `subscribe` API and emit a custom span per navigation.
 */
export function instrumentRouter<R extends AnyRouter>(router: R): R {
  if (!initialized || env.sentryDsn === null) return router;

  router.subscribe('onResolved', (event) => {
    const path = event.toLocation?.pathname;
    if (!path) return;
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: path,
      level: 'info',
    });
  });

  return router;
}

export { Sentry };
